import { hasSegmentFlag } from '@/data/flags/local-flags';
import { getActiveSubscription } from '@/data/session/session-globals';
import {
    DEFAULT_PERMISSIONS_REDIRECT,
    hasPredictRedirect,
} from '@/vue-router/route-permissions.utils';

const SEGMENT_FLAG_EVENTS = [
    'pendo_segmentFlagsReady',
    'pendo_segmentFlagsUpdated',
    'pendo_segmentFlagsError',
];
const PERMISSION_RESOLUTION_TIMEOUT = 1500;
const PREDICT_ROUTE_NAMES = {
    riskAdvisor: { home: 'RiskAdvisor', settings: 'RiskAdvisorSettings' },
    adoptionAgent: { home: 'AdoptionAgent', settings: 'AdoptionAgentSettings' },
};

export function predictRouteNameFor (page, isAdoptionAgent) {
    return PREDICT_ROUTE_NAMES[isAdoptionAgent ? 'adoptionAgent' : 'riskAdvisor'][page];
}

export function predictRouteName (page) {
    return predictRouteNameFor(page, hasSegmentFlag('adoptionAgent'));
}

export function switchSubscriptionToRiskAdvisor ({ ignoreQuery = false } = {}) {
    return function switchPredictSubscription ({ params, query }) {
        if (ignoreQuery) {
            query = {
                ...(query.locale && { locale: query.locale }),
                ...(query.version && { version: query.version }),
            };
        }

        return {
            name: 'RiskAdvisorSubscriptionSwitch',
            params,
            query,
        };
    };
}

export function resolvePredictSubscriptionSwitch (to) {
    return new Promise((resolve) => {
        let timeoutId;
        const noAccessRoute = () => ({
            ...DEFAULT_PERMISSIONS_REDIRECT,
            params: {
                ...DEFAULT_PERMISSIONS_REDIRECT.params,
                sub: to.params.sub,
            },
        });
        const currentSubscriptionMatchesPendo = () => {
            const activeSubscriptionName = getActiveSubscription()?.name;
            const account = window.pendo?.getSerializedMetadata?.()?.account;

            return Boolean(
                activeSubscriptionName &&
                    (account?.id === activeSubscriptionName ||
                        account?.impersonationSubName === activeSubscriptionName),
            );
        };
        function cleanup () {
            SEGMENT_FLAG_EVENTS.forEach((eventName) => {
                document.removeEventListener(eventName, onSegmentFlagsChanged);
            });
            window.clearTimeout(timeoutId);
        }
        function finish (route) {
            cleanup();
            resolve(route);
        }
        function onSegmentFlagsChanged (event) {
            if (!currentSubscriptionMatchesPendo()) return;

            if (event.type === 'pendo_segmentFlagsError') {
                finish(noAccessRoute());

                return;
            }

            if (event.type !== 'pendo_segmentFlagsUpdated') return;

            const redirect = hasPredictRedirect();
            if (redirect) {
                finish({
                    ...redirect,
                    params: {
                        ...redirect.params,
                        sub: to.params.sub,
                    },
                });

                return;
            }

            finish({
                name: predictRouteName('home'),
                params: { sub: to.params.sub },
                query: to.query,
            });
        }

        SEGMENT_FLAG_EVENTS.forEach((eventName) => {
            document.addEventListener(eventName, onSegmentFlagsChanged);
        });
        timeoutId = window.setTimeout(() => {
            finish(noAccessRoute());
        }, PERMISSION_RESOLUTION_TIMEOUT);
    });
}

export function redirectToCanonicalPredictRoute (page) {
    return function canonicalPredictRoute (to) {
        const name = predictRouteName(page);

        if (name === to.name) {
            return true;
        }

        return {
            name,
            params: to.params,
            query: to.query,
            hash: to.hash,
        };
    };
}
